// Framework Assessment renderer — v3.
// Uses the shared Advisor AI-content design system (same conventions as the
// AI & Automation Opportunities renderer): ai-doc-header, hero with eyebrow /
// headline / summary-text, flat white cards, tinted pills, evidence rows and
// phase list. Palette stays on the standard ADS hexes used by the other
// renderers so themeAiHtml hex-remapping applies uniformly.
const esc = (v) => String(v ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
const arr = (a) => Array.isArray(a) ? a : [];
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };
const embeddedJsonScript = (json) => `<script id="__qa_data__" type="application/json">${JSON.stringify(json || {}).replace(/</g, '\u003C')}</script>`;

const LEVELS = ['Initial', 'Emerging', 'Defined', 'Managed', 'Optimized'];
// Tinted [bg, text, border] per level — consistent with badge tints used across AI content.
const LEVEL_TINTS = {
  1: ['#FFEBE6', '#BF2600', '#FF8F73'],
  2: ['#FFF0B3', '#974F0C', '#FFE380'],
  3: ['#DEEBFF', '#0747A6', '#B3D4FF'],
  4: ['#E3FCEF', '#006644', '#ABF5D1'],
  5: ['#E3FCEF', '#006644', '#57D9A3'],
};
// Saturated bar fills (small areas only).
const LEVEL_BAR = { 1: '#DE350B', 2: '#FF8B00', 3: '#0052CC', 4: '#36B37E', 5: '#00875A' };
const clampScore = (s) => Math.min(5, Math.max(1, Math.round(num(s) || 1)));
const levelForScore = (s) => LEVELS[clampScore(s) - 1];
const tintFor = (s) => LEVEL_TINTS[clampScore(s)];
const barFor = (s) => LEVEL_BAR[clampScore(s)];

const PRIORITY_TINTS = {
  High:   ['#FFEBE6', '#BF2600', '#FF8F73'],
  Medium: ['#FFF0B3', '#974F0C', '#FFE380'],
  Low:    ['#E3FCEF', '#006644', '#ABF5D1'],
};

const pill = (text, [bg, fg, border], extra = '') =>
  `<span style="display:inline-flex;align-items:center;padding:4px 11px;border-radius:999px;font-size:12px;font-weight:700;background:${bg};color:${fg};border:1px solid ${border};${extra}">${text}</span>`;

const sectionHdr = (icon, iconBg, title) => `
  <div class="section-hdr">
    <div class="section-icon" style="background:${iconBg};">${icon}</div>
    <div class="section-title">${esc(title)}</div>
  </div>`;

const scaleLegend = (currentScore, targetScore) => {
  const cur = num(currentScore); const tgt = num(targetScore);
  const seg = (i) => {
    const active = cur === i; const isTarget = tgt === i && tgt !== cur;
    const [bg, fg, border] = LEVEL_TINTS[i];
    const style = active
      ? `background:${bg};color:${fg};border:2px solid ${border};`
      : isTarget
        ? `background:#fff;color:#42526E;border:2px dashed #6B778C;`
        : `background:#FAFBFC;color:#6B778C;border:1px solid #EBECF0;`;
    return `<div style="flex:1;text-align:center;padding:9px 4px;border-radius:8px;${style}">
      <div style="font-size:15px;font-weight:800;">${i}</div>
      <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;">${LEVELS[i-1]}</div>
      ${active ? '<div style="font-size:9px;font-weight:700;margin-top:2px;">CURRENT</div>' : (isTarget ? '<div style="font-size:9px;font-weight:700;margin-top:2px;">90-DAY TARGET</div>' : '')}
    </div>`;
  };
  return `<div style="display:flex;gap:8px;">${[1,2,3,4,5].map(seg).join('')}</div>`;
};

const domainBar = (d) => {
  const score = clampScore(d.score);
  const target = num(d.target_score);
  const pct = (score / 5) * 100;
  const tick = target && target !== score
    ? `<div title="90-day target: ${target}" style="position:absolute;left:calc(${Math.min(100,(target/5)*100)}% - 2px);top:-3px;bottom:-3px;width:3px;background:#42526E;border-radius:2px;"></div>`
    : '';
  return `
  <div style="display:grid;grid-template-columns:minmax(180px,1fr) 3fr auto;gap:12px;align-items:center;padding:9px 0;border-top:1px solid #EBECF0;">
    <div>
      <div style="font-weight:700;font-size:13px;color:#172B4D;">${esc(d.domain)}</div>
      ${d.practice_reference ? `<div style="font-size:11px;font-weight:700;color:#6554C0;">${esc(d.practice_reference)}</div>` : ''}
    </div>
    <div style="position:relative;height:12px;background:#EBECF0;border-radius:999px;">
      <div style="position:absolute;left:0;top:0;bottom:0;width:${pct}%;background:${barFor(score)};border-radius:999px;"></div>
      ${tick}
    </div>
    <div style="font-size:12px;font-weight:700;color:#172B4D;white-space:nowrap;">${score} / 5${target && target !== score ? ` <span style="color:#6B778C;font-weight:600;">→ ${target}</span>` : ''}</div>
  </div>`;
};

const listCell = (label, items, emptyText) => `
  <div class="opp-cell">
    <div class="cell-label">${label}</div>
    ${items.length
      ? items.map(x => `<div class="cell-text" style="padding:3px 0;">· ${esc(x)}</div>`).join('')
      : `<div class="cell-text" style="font-style:italic;color:#6B778C;">${emptyText}</div>`}
  </div>`;

const domainCard = (d, i) => {
  const score = clampScore(d.score);
  const target = num(d.target_score);
  return `
  <div class="opp-card">
    <div class="opp-top">
      <div class="opp-num">Domain ${i + 1}</div>
      <div class="opp-title">${esc(d.domain)}</div>
      <div class="opp-badges">
        ${pill(`${esc(d.maturity_level || levelForScore(score))} · ${score}/5`, tintFor(score))}
        ${target && target !== score ? pill(`Target: ${target}/5 · ${esc(levelForScore(target))}`, ['#FAFBFC', '#42526E', '#6B778C'], 'border-style:dashed;') : ''}
      </div>
      ${d.practice_reference ? `<div style="font-size:12px;font-weight:700;color:#6554C0;">${esc(d.practice_reference)}</div>` : ''}
    </div>
    <div class="opp-body">
      ${listCell('Strengths', arr(d.strengths), 'No notable strengths evidenced yet.')}
      ${listCell('Gaps', arr(d.gaps), '—')}
    </div>
    ${arr(d.evidence).length ? `
    <div class="evidence-row">
      <div class="evidence-label">Evidence</div>
      ${arr(d.evidence).map(x => `<div class="evidence-item">${esc(x)}</div>`).join('')}
    </div>` : ''}
  </div>`;
};

const recCard = (r) => `
  <div class="opp-card">
    <div class="opp-top" style="border-bottom:none;">
      <div style="display:flex;justify-content:space-between;gap:10px;align-items:flex-start;flex-wrap:wrap;">
        <div class="opp-title" style="margin-bottom:0;">${esc(r.title)}</div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          ${r.priority ? pill(esc(r.priority), PRIORITY_TINTS[r.priority] || ['#F4F5F7','#42526E','#DFE1E6']) : ''}
          ${r.time_horizon ? pill(esc(r.time_horizon), ['#F4F5F7','#42526E','#DFE1E6']) : ''}
        </div>
      </div>
      ${r.domain ? `<div style="margin-top:6px;"><span class="link-pill">${esc(r.domain)}</span></div>` : ''}
      <div class="cell-text" style="margin-top:8px;">${esc(r.recommendation || '')}</div>
    </div>
    ${r.expected_outcome ? `<div class="config-row"><strong>Expected outcome:</strong> ${esc(r.expected_outcome)}</div>` : ''}
  </div>`;

const roadmapPhase = (n, name, items) => `
  <div class="phase">
    <div class="phase-top">
      <div class="phase-num" style="background:${['#0052CC','#6554C0','#00875A'][n-1]};">${n}</div>
      <div class="phase-meta">
        <div class="phase-name">${name}</div>
        ${arr(items).length
          ? `<div class="phase-focus">${arr(items).map(esc).join(' · ')}</div>`
          : '<div class="phase-focus" style="font-style:italic;">No actions scheduled.</div>'}
      </div>
    </div>
  </div>`;

export function renderFrameworkAssessmentHTML(data, processTitle = 'Process') {
  const overall = data?.overall_maturity || {};
  const domains = arr(data?.domain_assessments);
  const recs = arr(data?.recommendations);
  const roadmap = data?.roadmap || {};
  const score = num(overall.score);
  const target = num(overall.target_score);
  const framework = data?.framework || 'Framework';
  const timestamp = new Date().toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"/>
<meta name="renderer-version" content="v2026-framework-assessment-v3"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>${esc(framework)} Assessment${processTitle ? ` — ${esc(processTitle)}` : ''}</title>
<style>
  html,body{margin:0;padding:0;box-sizing:border-box;}
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI','Helvetica Neue',Arial,sans-serif;background:#F4F5F7;color:#172B4D;}
  .wrap{display:flex;flex-direction:column;gap:0;padding:10px;}
  .ai-doc-header{background:#fff;border:1px solid #E8E9EB;border-radius:10px;padding:14px 20px;margin-bottom:10px;display:flex;justify-content:space-between;align-items:center;}
  .ai-doc-header-left{display:flex;flex-direction:column;gap:4px;}
  .ai-doc-header-title{font-size:16px;font-weight:700;color:#172B4D;}
  .ai-doc-header-type{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.08em;color:#0052CC;}
  .ai-doc-header-time{font-size:12px;color:#6B778C;}
  .card{background:#fff;border:1px solid #E8E9EB;border-radius:10px;padding:18px 20px;margin-bottom:10px;}
  .hero{background:#fff;border:1px solid #E8E9EB;border-radius:10px;padding:20px 22px;position:relative;overflow:hidden;margin-bottom:10px;}
  .hero::before{content:'';position:absolute;top:0;left:0;right:0;height:4px;background:linear-gradient(90deg,#6554C0,#0052CC,#00875A);}
  .eyebrow{font-size:10px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#6554C0;margin-bottom:10px;display:flex;align-items:center;gap:6px;}
  .eyebrow::before{content:'';display:inline-block;width:16px;height:2px;background:#6554C0;border-radius:2px;}
  .headline{font-size:22px;font-weight:800;line-height:1.25;color:#172B4D;margin-bottom:10px;}
  .summary-text{font-size:14px;line-height:1.7;color:#42526E;padding-bottom:14px;border-bottom:1px solid #EBECF0;margin-bottom:14px;}
  .meta-row{display:flex;align-items:center;flex-wrap:wrap;gap:10px;margin-bottom:14px;}
  .section-hdr{display:flex;align-items:center;gap:10px;margin-bottom:16px;}
  .section-icon{width:34px;height:34px;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0;font-size:15px;}
  .section-title{font-size:17px;font-weight:700;color:#172B4D;}
  .opp-card{border:1px solid #EBECF0;border-radius:10px;background:#fff;margin-bottom:10px;overflow:hidden;}
  .opp-card:last-child{margin-bottom:0;}
  .opp-top{padding:14px 16px;border-bottom:1px solid #EBECF0;}
  .opp-num{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#6B778C;margin-bottom:5px;}
  .opp-title{font-size:16px;font-weight:700;color:#172B4D;margin-bottom:8px;}
  .opp-badges{display:flex;flex-wrap:wrap;gap:5px;margin-bottom:8px;}
  .opp-body{display:grid;grid-template-columns:1fr 1fr;}
  .opp-cell{padding:12px 16px;border-right:1px solid #EBECF0;}
  .opp-cell:nth-child(2n){border-right:none;}
  .cell-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:5px;}
  .cell-text{font-size:13px;line-height:1.65;color:#42526E;}
  .config-row{padding:10px 16px;background:#F8F9FF;border-top:1px solid #EBECF0;font-size:12px;line-height:1.6;color:#0747A6;}
  .config-row strong{font-weight:700;}
  .evidence-row{padding:10px 16px;background:#FAFBFC;border-top:1px solid #EBECF0;}
  .evidence-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:5px;}
  .evidence-item{font-size:12px;color:#42526E;line-height:1.5;padding:3px 0 3px 10px;border-left:2px solid #EBECF0;margin-bottom:3px;}
  .link-pill{padding:3px 9px;border-radius:999px;background:#EAE6FF;color:#5243AA;font-size:11px;font-weight:700;}
  .phase-list{display:flex;flex-direction:column;gap:8px;}
  .phase{border:1px solid #EBECF0;border-radius:8px;background:#FAFBFC;overflow:hidden;}
  .phase-top{display:flex;align-items:center;gap:12px;padding:12px 14px;}
  .phase-num{width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:800;color:#fff;flex-shrink:0;}
  .phase-meta{flex:1;}
  .phase-name{font-size:14px;font-weight:700;color:#172B4D;}
  .phase-focus{font-size:12px;color:#42526E;margin-top:2px;}
  .two-col{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
  @media(max-width:860px){.two-col{grid-template-columns:1fr;}.opp-body{grid-template-columns:1fr;}.opp-cell{border-right:none;border-bottom:1px solid #EBECF0;}}
</style></head><body>
<div class="wrap">
<div class="ai-doc-header">
  <div class="ai-doc-header-left">
    <div class="ai-doc-header-type">${esc(framework)} Assessment</div>
    <div class="ai-doc-header-title">${esc(processTitle)}</div>
  </div>
  <div class="ai-doc-header-time">${timestamp}</div>
</div>
<div class="hero">
  <div class="eyebrow">Framework Assessment</div>
  <div class="headline">${esc(framework)} Maturity Assessment — ${esc(processTitle)}</div>
  ${data?.executive_summary ? `<div class="summary-text">${esc(data.executive_summary)}</div>` : ''}
  <div class="meta-row">
    ${score !== null ? pill(`Maturity: ${score}/5 · ${esc(overall.level || levelForScore(score))}`, tintFor(score), 'font-size:13px;padding:6px 14px;') : ''}
    ${target !== null && target !== score ? pill(`90-Day Target: ${target}/5 · ${esc(levelForScore(target))}`, ['#FAFBFC', '#42526E', '#6B778C'], 'font-size:13px;padding:6px 14px;border-style:dashed;') : ''}
    ${data?.scope ? pill(esc(data.scope), ['#F4F5F7', '#42526E', '#DFE1E6'], 'font-size:13px;padding:6px 14px;') : ''}
  </div>
  ${scaleLegend(score, target)}
  ${overall.rationale ? `<div style="margin-top:14px;font-size:13px;line-height:1.65;color:#42526E;">${esc(overall.rationale)}</div>` : ''}
  ${overall.target_rationale ? `<div class="config-row" style="margin-top:10px;border:1px dashed #B3D4FF;border-radius:8px;"><strong>Path to ${target !== null ? `${target}/5` : 'target'}:</strong> ${esc(overall.target_rationale)}</div>` : ''}
</div>
${domains.length ? `
<div class="card">
  ${sectionHdr('📊', '#0052CC', 'Maturity Scorecard')}
  ${domains.map(domainBar).join('')}
</div>` : ''}
${domains.length ? `
<div class="card">
  ${sectionHdr('🧭', '#6554C0', 'Domain Assessments')}
  ${domains.map(domainCard).join('')}
</div>` : ''}
${arr(data?.priority_gaps).length ? `
<div class="card">
  ${sectionHdr('⚠️', '#DE350B', 'Priority Gaps')}
  ${arr(data.priority_gaps).map(x => `<div class="evidence-item" style="font-size:13px;padding:5px 0 5px 12px;border-left-color:#FF8F73;">${esc(x)}</div>`).join('')}
</div>` : ''}
${recs.length ? `
<div class="card">
  ${sectionHdr('🛠️', '#00875A', 'Recommendations')}
  ${recs.map(recCard).join('')}
</div>` : ''}
<div class="card">
  ${sectionHdr('🗓️', '#0052CC', '30 / 60 / 90 Day Roadmap')}
  <div class="phase-list">
    ${roadmapPhase(1, '0–30 days', roadmap.days_0_30)}
    ${roadmapPhase(2, '30–60 days', roadmap.days_30_60)}
    ${roadmapPhase(3, '60–90 days', roadmap.days_60_90)}
  </div>
</div>
</div>
${embeddedJsonScript(data)}
</body></html>`;
}

export const buildFrameworkAssessmentExportHTML = renderFrameworkAssessmentHTML;
